import { useState } from 'react';
import { notesApiService } from '../services/notesApi';
import { X, Loader, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { CaseLinkField } from './logbook/CaseLinkField';
import { linkRecordToCase } from '../utils/logbookCaseLink';
import type { CaseChoice } from './logbook/CaseChooser';

interface CreateNoteWarningModalProps {
  playerUuid?: string;
  type: 'note' | 'warning';
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateNoteWarningModal = ({ playerUuid: initialPlayerUuid, type, onClose, onSuccess }: CreateNoteWarningModalProps) => {
  const [playerUuid, setPlayerUuid] = useState(initialPlayerUuid || '');
  const [content, setContent] = useState('');
  const [caseChoice, setCaseChoice] = useState<CaseChoice | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  /** The entry saved but its case did not. Said plainly rather than folded into the error. */
  const [caseWarning, setCaseWarning] = useState<string | null>(null);
  const [linkedCase, setLinkedCase] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      setError('Content cannot be empty.');
      return;
    }
    if (!playerUuid.trim()) {
      setError('Player UUID cannot be empty.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setCaseWarning(null);
    setIsSuccess(false);

    try {
      const created = await notesApiService.createEntry({
        uuid: playerUuid,
        type: type,
        content: content,
      });

      // The entry exists from here on, so a case that cannot be attached is reported as a failed
      // link rather than a failed save; otherwise it gets written a second time.
      let linked: string | null = null;
      let failed: string | null = null;
      if (caseChoice) {
        try {
          const target = await linkRecordToCase(caseChoice, type, created.id, playerUuid.trim());
          linked = target.reference;
        } catch {
          failed = `The ${type} was saved, but it could not be attached to the case. Attach it from the ${type} itself.`;
        }
      }

      setLinkedCase(linked);
      setCaseWarning(failed);
      setIsSuccess(true);
      onSuccess();
      // The case outcome is worth reading before the modal closes itself.
      setTimeout(() => {
        onClose();
      }, linked || failed ? 2500 : 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = type === 'note' ? 'Create Note' : 'Create Warning';
  const Icon = type === 'warning' ? (
    <span className="icon-tile icon-tile-lg icon-tile-warning mb-3">
      <AlertTriangle className="w-5 h-5" />
    </span>
  ) : null;
  
  return (
    <div className="modal-overlay z-50">
      <form onSubmit={handleSubmit} className="modal-panel max-w-lg">
        <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
        
        <div className="modal-body">
          <div className="flex flex-col items-center text-center">
            {Icon}
            <h2 className="modal-title mb-1">{title}</h2>
            {!initialPlayerUuid && (
              <p className="text-sm text-glass-400 mb-5">Enter a Player UUID to create a new {type}.</p>
            )}
          </div>

          <div className="space-y-4 mt-4">
            {!initialPlayerUuid && (
               <div>
                  <label className="block text-xs font-medium text-glass-400 mb-1.5">Player UUID</label>
                  <input 
                      type="text" 
                      value={playerUuid} 
                      onChange={e => setPlayerUuid(e.target.value)} 
                      placeholder="Enter the player's UUID"
                      className="form-input font-mono"
                      required 
                  />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`Enter the content of the ${type}...`}
                className="glass-input w-full min-h-[10rem]"
                disabled={isSubmitting || isSuccess}
              />
            </div>

            <CaseLinkField
              value={caseChoice}
              onChange={setCaseChoice}
              subject={playerUuid.trim() || undefined}
            />
          </div>

          {error && (
            <div className="alert alert-danger mt-4" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          {caseWarning && (
            <div className="alert alert-warning mt-4" role="alert">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{caseWarning}</span>
            </div>
          )}

          {linkedCase && (
            <div className="alert alert-success mt-4" role="status">
              <CheckCircle className="w-4 h-4 shrink-0" />
              <span>Attached to <span className="font-mono">{linkedCase}</span>.</span>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="glass-button-secondary">Cancel</button>
          <button type="submit" disabled={isSubmitting || isSuccess} className="btn-primary">
            {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
            {isSuccess && <CheckCircle className="w-4 h-4" />}
            {isSubmitting ? 'Submitting...' : isSuccess ? 'Success!' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};
